import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// Configuration globale optimisée pour les requêtes
export const OPTIMIZED_QUERY_CONFIG = {
  staleTime: 5 * 60 * 1000, // 5 minutes - les données restent "fraîches" pendant 5 min
  gcTime: 15 * 60 * 1000, // 15 minutes - les données sont gardées en cache pendant 15 min
  refetchOnWindowFocus: false, // Pas de re-fetch automatique
  refetchOnMount: false, // Pas de re-fetch au montage si les données sont en cache
  refetchOnReconnect: false, // Pas de re-fetch automatique sur reconnexion
  retry: 2, // Seulement 2 tentatives en cas d'erreur
  retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
};

// Hook pour les inspirations optimisé
export const useOptimizedInspirations = () => {
  return useQuery({
    queryKey: ['inspirations-optimized'],
    queryFn: async () => {
      console.log('🔄 Chargement optimisé des inspirations...');
      
      const { data, error } = await supabase
        .from('inspirations_new')
        .select('id, titre, description, image_url, categorie, ordre, afficher_accueil')
        .eq('afficher_accueil', true)
        .order('ordre', { ascending: true })
        .limit(8); // Limiter pour de meilleures performances
      
      if (error) {
        console.error('❌ Erreur lors du chargement des inspirations:', error);
        throw error;
      }
      
      console.log('✅ Inspirations chargées:', data?.length || 0);
      return data || [];
    },
    ...OPTIMIZED_QUERY_CONFIG,
  });
};

// Hook pour les catégories d'évènements optimisé
export const useOptimizedEventCategories = () => {
  return useQuery({
    queryKey: ['event-categories-optimized'],
    queryFn: async () => {
      console.log('🔄 Chargement optimisé des catégories...');
      
      const { data, error } = await supabase
        .from('event_categories')
        .select('id, title, description, image_url, base_price, type_categorie, is_active')
        .eq('is_active', true)
        .order('order', { ascending: true })
        .limit(6);
      
      if (error) {
        console.error('❌ Erreur lors du chargement des catégories:', error);
        throw error;
      }
      
      console.log('✅ Catégories chargées:', data?.length || 0);
      return data || [];
    },
    ...OPTIMIZED_QUERY_CONFIG,
  });
};

// Hook pour précharger les données critiques
export const usePreloadCriticalData = () => {
  const queryClient = useQueryClient();

  const preloadData = async () => {
    // Précharger les offers si pas encore en cache
    if (!queryClient.getQueryData(['optimized-offers', false])) {
      queryClient.prefetchQuery({
        queryKey: ['optimized-offers', false],
        queryFn: async () => {
          const { data } = await supabase
            .from('offers')
            .select('id, title, description, category, image_url, is_active, quantifiable, display_order')
            .eq('is_active', true)
            .order('display_order', { ascending: true });
          return data || [];
        },
        ...OPTIMIZED_QUERY_CONFIG,
      });
    }

    // Précharger les settings si pas encore en cache
    if (!queryClient.getQueryData(['settings'])) {
      queryClient.prefetchQuery({
        queryKey: ['settings'],
        queryFn: async () => {
          const { data } = await supabase
            .from('settings')
            .select('*')
            .single();
          return data;
        },
        ...OPTIMIZED_QUERY_CONFIG,
      });
    }
  };

  return { preloadData };
};

// Hook utilitaire pour nettoyer le cache
export const useCacheOptimization = () => {
  const queryClient = useQueryClient();

  const optimizeCache = () => {
    // Supprimer les requêtes anciennes (plus de 1 heure)
    queryClient.getQueryCache().getAll().forEach(query => {
      const queryAge = Date.now() - (query.state.dataUpdatedAt || 0);
      if (queryAge > 60 * 60 * 1000) { // 1 heure
        queryClient.removeQueries({ queryKey: query.queryKey });
      }
    });

    // Invalider les données très anciennes
    queryClient.invalidateQueries({
      predicate: (query) => {
        const queryAge = Date.now() - (query.state.dataUpdatedAt || 0);
        return queryAge > 30 * 60 * 1000; // 30 minutes
      }
    });
  };

  return { optimizeCache };
};